import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Check, Clock, BookOpen, FlaskConical, Award, Download, ShieldCheck, HelpCircle, Zap, Sparkles, TrendingUp, Users, Target, Lock, Star, ArrowRight, Plus } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
import { TestimonialCard } from '../components/TestimonialCard';
import { PurchaseNotification } from '../components/PurchaseNotification';
// Enhanced animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const fadeInScale = {
  hidden: {
    opacity: 0,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export const LandingPage: React.FC = () => {
  const scrollToPricing = () => {
    const element = document.getElementById('compra');
    if (element) {
      const yOffset = -20;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-white font-sans text-gray-900 overflow-x-hidden" data-id="element-33">
      <PurchaseNotification data-id="element-34" />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-blue-50 via-white to-white pt-6 pb-8 px-4 overflow-hidden" data-id="element-35">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" data-id="element-36">
          <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(59, 130, 246) 1px, transparent 0)`,
          backgroundSize: '48px 48px'
        }} data-id="element-37"></div>
        </div>

        <div className="container mx-auto max-w-6xl relative z-10" data-id="element-38">
          <div className="text-center" data-id="element-39">
            <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-5 py-2.5 rounded-full text-sm font-semibold mb-6 border border-blue-200 shadow-sm" data-id="element-40">
              <Sparkles className="w-4 h-4" data-id="element-41" />
              <span data-id="element-42">PARA DOCENTES DE FÍSICA</span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="text-3xl md:text-5xl lg:text-6xl font-black mb-3 leading-[1.1] text-gray-900 tracking-tight" data-id="element-43">
              ORGANIZA TODO TU AÑO DE <br className="hidden md:block" data-id="element-44" />
              <span className="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent" data-id="element-45">
                FÍSICA SIN ESTRÉS
              </span>
            </motion.h1>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex justify-center my-4" data-id="element-46">
              <div className="relative group" data-id="element-47">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 to-blue-600 rounded-3xl opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-500" data-id="element-48"></div>
                <img src="https://cdn.magicpatterns.com/uploads/jTTPoujFBtRhSE8WJnkV4d/1.png" alt="Mega Pack Física en Acción Producto Principal" className="relative w-full max-w-[280px] md:max-w-[380px] drop-shadow-2xl hover:scale-[1.02] transition-transform duration-500 cursor-pointer" onClick={scrollToPricing} data-id="element-49" />
              </div>
            </motion.div>

            <motion.h2 initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4,
            duration: 0.6
          }} className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-50">
              MEGA PACK FÍSICA EN ACCIÓN
            </motion.h2>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.5,
            duration: 0.6
          }} className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-6 leading-relaxed" data-id="element-51">
              Sistema completo de proyectos, experiencias y evaluaciones listas
              para usar.
              <br className="hidden md:block" data-id="element-52" />
              <span className="font-semibold text-gray-700" data-id="element-53">
                Todo tu año de Física resuelto
              </span>{' '}
              (1.º a 6.º año)
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6,
            duration: 0.6
          }} data-id="element-54">
              <button onClick={scrollToPricing} className="inline-flex flex-col items-center justify-center bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold uppercase tracking-wide py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-b-4 border-blue-900" data-id="element-55">
                <span className="text-lg" data-id="element-56">QUIERO ACCESO INMEDIATO</span>
                <span className="text-xs font-semibold opacity-90 mt-1 normal-case" data-id="element-57">
                  Pago 100% seguro y encriptado
                </span>
              </button>
            </motion.div>

            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.8,
            duration: 0.6
          }} className="flex flex-wrap items-center justify-center gap-6 mt-6 text-sm text-gray-500" data-id="element-58">
              <div className="flex items-center gap-2" data-id="element-59">
                <Check className="w-4 h-4 text-green-500" data-id="element-60" />
                <span data-id="element-61">Descarga inmediata</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-62">
                <Check className="w-4 h-4 text-green-500" data-id="element-63" />
                <span data-id="element-64">Garantía 7 días</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-65">
                <Check className="w-4 h-4 text-green-500" data-id="element-66" />
                <span data-id="element-67">Acceso de por vida</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-12 md:py-16 px-4 bg-white" data-id="element-68">
        <div className="container mx-auto max-w-5xl" data-id="element-69">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-70">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-71">
              Si enseñas Física, esto te va a <br className="hidden md:block" data-id="element-72" />
              <span className="text-blue-600" data-id="element-73">sonar muy familiar…</span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto" data-id="element-74">
              Llegas cansado. Sabes que tienes que planificar. Abres la
              computadora.
              <br className="hidden md:block" data-id="element-75" />
              <span className="font-semibold text-gray-700" data-id="element-76">
                Y una vez más empiezas desde cero.
              </span>
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-77">
            <motion.div variants={staggerItem} className="bg-gradient-to-br from-red-50 to-orange-50 p-8 rounded-3xl border border-red-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-78">
              <div className="flex items-center gap-3 mb-4" data-id="element-79">
                <div className="bg-red-100 p-3 rounded-2xl" data-id="element-80">
                  <Clock className="w-6 h-6 text-red-600" data-id="element-81" />
                </div>
                <h3 className="text-xl font-bold text-red-700" data-id="element-82">
                  El Ciclo del Agotamiento
                </h3>
              </div>
              <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-83">
                Buscando proyectos. Buscando experiencias prácticas. Buscando
                actividades que motiven. Buscando evaluaciones. Buscando cómo
                explicar sin que la Física quede abstracta.
              </p>
              <p className="text-gray-800 font-semibold" data-id="element-84">
                El tiempo pasa rápido. El cansancio aumenta. Y tu mente no se
                detiene.
              </p>
            </motion.div>

            <motion.div variants={staggerItem} className="bg-gradient-to-br from-gray-50 to-slate-50 p-8 rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-85">
              <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-86">
                El problema real
              </h3>
              <div className="space-y-3" data-id="element-87">
                {['Planificar', 'Diseñar experiencias', 'Preparar evaluaciones', 'Cumplir programas', 'Organizar ferias', 'Registrar avances', 'Motivar estudiantes'].map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.05
              }} className="flex items-center gap-3 text-gray-700" data-id="element-88">
                    <div className="w-2 h-2 rounded-full bg-red-400" data-id="element-89"></div>
                    <span data-id="element-90">{item}</span>
                  </motion.div>)}
              </div>
              <p className="mt-6 font-bold text-gray-900 text-lg" data-id="element-91">
                Todo al mismo tiempo.
              </p>
            </motion.div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInScale} className="text-center bg-gradient-to-br from-blue-50 to-indigo-50 p-10 rounded-3xl border border-blue-100 shadow-lg" data-id="element-92">
            <p className="text-xl md:text-2xl text-gray-800 leading-relaxed" data-id="element-93">
              No es porque no sepas enseñar. No es porque no seas un buen
              docente.
              <br className="hidden md:block" data-id="element-94" />
              <span className="font-bold text-blue-600 text-2xl md:text-3xl block mt-3" data-id="element-95">
                Es porque estás intentando sostener todo el año sin un sistema
                que te sostenga a ti.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-white to-blue-50" data-id="element-96">
        <div className="container mx-auto max-w-6xl" data-id="element-97">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-98">
            <span className="inline-block bg-blue-600 text-white font-bold px-5 py-2 rounded-full text-sm uppercase tracking-wider mb-6 shadow-lg" data-id="element-99">
              La Solución Definitiva
            </span>
            <h3 className="text-4xl md:text-6xl font-black mb-6 text-gray-900 tracking-tight" data-id="element-100">
              MEGA PACK <br className="md:hidden" data-id="element-101" />
              <span className="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent" data-id="element-102">
                FÍSICA EN ACCIÓN™
              </span>
            </h3>
            <p className="text-2xl md:text-3xl text-gray-700 max-w-3xl mx-auto font-medium leading-relaxed" data-id="element-103">
              Un sistema anual completo de Física basado en la experimentación
              real.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16" data-id="element-104">
            {[{
            icon: Check,
            text: 'Todo listo',
            color: 'from-green-500 to-emerald-600'
          }, {
            icon: FlaskConical,
            text: 'Todo probado',
            color: 'from-blue-500 to-blue-600'
          }, {
            icon: BookOpen,
            text: 'Todo organizado',
            color: 'from-purple-500 to-purple-600'
          }, {
            icon: Zap,
            text: 'Todo usable',
            color: 'from-orange-500 to-orange-600'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} className="bg-white p-6 md:p-8 rounded-2xl border border-blue-100 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1" data-id="element-105">
                <div className={`bg-gradient-to-br ${item.color} w-14 h-14 rounded-2xl flex items-center justify-center mb-4 mx-auto`} data-id="element-106">
                  <item.icon className="w-7 h-7 text-white" data-id="element-107" />
                </div>
                <span className="font-bold text-lg text-gray-900 block" data-id="element-108">
                  {item.text}
                </span>
              </motion.div>)}
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInScale} className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border border-blue-100" data-id="element-109">
            <h4 className="text-3xl font-bold text-blue-600 mb-10 text-center flex items-center justify-center gap-3" data-id="element-110">
              <Sparkles className="w-8 h-8" data-id="element-111" />
              ¿Qué incluye el Mega Pack?
            </h4>
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-6" data-id="element-112">
              {['Proyectos anuales completos para cada curso', 'Cuadernillos por año con experiencias reales', 'Más de 60 fichas editables de actividades y evaluaciones', 'Planillas de seguimiento con criterios de evaluación', 'Videos demostrativos y recursos visuales', 'Material adaptable para imprimir, editar o proyectar', 'Recursos para ferias, exposiciones y muestras escolares'].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.08
            }} className="flex items-start gap-4 group" data-id="element-113">
                  <div className="bg-green-100 p-2 rounded-xl mt-0.5 group-hover:scale-110 transition-transform" data-id="element-114">
                    <Check className="w-5 h-5 text-green-600" data-id="element-115" />
                  </div>
                  <span className="text-lg text-gray-700 leading-relaxed" data-id="element-116">
                    {item}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 px-4 bg-blue-50" data-id="element-117">
        <div className="container mx-auto max-w-6xl" data-id="element-118">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-3xl md:text-5xl font-bold text-center text-gray-900 mb-16" data-id="element-119">
            Resultados que <span className="text-blue-600" data-id="element-120">transforman</span>
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid md:grid-cols-2 gap-8" data-id="element-121">
            <motion.div variants={staggerItem} className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border-t-4 border-blue-500 hover:shadow-2xl transition-shadow" data-id="element-122">
              <div className="flex items-center gap-4 mb-8" data-id="element-123">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-2xl shadow-lg" data-id="element-124">
                  <Users className="w-8 h-8 text-white" data-id="element-125" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-blue-600" data-id="element-126">
                  Qué logran tus estudiantes
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-127">
                {['Comprender la Física desde la práctica', 'Conectar teoría con la realidad', 'Desarrollar razonamiento científico', 'Participar más en clase', 'Observar, registrar y analizar resultados', 'Valorar la Física en la vida cotidiana'].map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.06
              }} className="flex items-center gap-3 text-gray-700 text-lg" data-id="element-128">
                    <div className="bg-blue-100 p-1.5 rounded-lg" data-id="element-129">
                      <Check className="w-5 h-5 text-blue-600" data-id="element-130" />
                    </div>
                    {item}
                  </motion.li>)}
              </ul>
            </motion.div>

            <motion.div variants={staggerItem} className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border-t-4 border-green-500 hover:shadow-2xl transition-shadow" data-id="element-131">
              <div className="flex items-center gap-4 mb-8" data-id="element-132">
                <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-2xl shadow-lg" data-id="element-133">
                  <Target className="w-8 h-8 text-white" data-id="element-134" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-green-600" data-id="element-135">
                  Qué obtienes tú como docente
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-136">
                {['Ahorrar horas de planificación cada semana', 'Dejar de improvisar', 'Tener todo el año organizado', 'Enseñar con claridad y seguridad', 'Llegar al cierre del ciclo sin estrés', 'Destacarte con proyectos innovadores'].map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.06
              }} className="flex items-center gap-3 text-gray-700 text-lg" data-id="element-137">
                    <div className="bg-green-100 p-1.5 rounded-lg" data-id="element-138">
                      <Check className="w-5 h-5 text-green-600" data-id="element-139" />
                    </div>
                    {item}
                  </motion.li>)}
              </ul>
            </motion.div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="mt-12 text-center" data-id="element-140">
            <CTAButton text="QUIERO LOGRAR ESTO" subtext="Pago 100% seguro y encriptado" className="mx-auto" onClick={scrollToPricing} data-id="element-141" />
          </motion.div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-16 md:py-24 px-4 bg-white" data-id="element-142">
        <div className="container mx-auto max-w-6xl" data-id="element-143">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-144">
            <span className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 font-bold px-6 py-2.5 rounded-full text-sm uppercase tracking-wider mb-6 shadow-lg" data-id="element-145">
              🎁 Oferta Especial
            </span>
            <h2 className="text-4xl md:text-5xl font-black mb-4 text-gray-900" data-id="element-146">
              BONOS INCLUIDOS —{' '}
              <span className="text-blue-600" data-id="element-147">GRATIS HOY</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-148">
              Al adquirir tu Mega Pack hoy, te llevas estos{' '}
              <span className="font-bold text-gray-900" data-id="element-149">
                3 regalos exclusivos
              </span>
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8" data-id="element-150">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/4KbjkFbxfSoGJ2E6KgrWGW/2.png',
            value: '$19.99',
            title: 'BONO 1 – Manual de la Física Tradicional',
            desc: 'Recurso completo de Física con amplio contenido visual.',
            color: 'from-blue-500 to-blue-600'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/77rEtYXjC7DumCQvGEFXP8/3.png',
            value: '$14.99',
            title: 'BONO 2 – Láminas y Fichas Editables',
            desc: 'Recursos visuales y editables en Canva para reforzar la comprensión.',
            color: 'from-purple-500 to-purple-600'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/ojGq5GQBhnQqxgj7daWY9Z/4.png',
            value: '$12.99',
            title: 'BONO 3 – Cuadernillos Editables',
            desc: 'Todo organizado, listo para imprimir y reutilizar cada año.',
            color: 'from-orange-500 to-orange-600'
          }].map((bonus, i) => <motion.div key={i} variants={staggerItem} className="group bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-6 border border-blue-100 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2" data-id="element-151">
                <div className="flex items-center justify-between mb-4" data-id="element-152">
                  <span className={`bg-gradient-to-r ${bonus.color} text-white font-bold text-xs px-3 py-1.5 rounded-full shadow-md`} data-id="element-153">
                    VALOR: {bonus.value} USD
                  </span>
                  <span className="bg-green-500 text-white font-bold text-xs px-3 py-1.5 rounded-full shadow-md animate-pulse" data-id="element-154">
                    GRATIS
                  </span>
                </div>
                <div className="relative mb-6 overflow-hidden rounded-2xl" data-id="element-155">
                  <img src={bonus.img} alt={bonus.title} className="w-full shadow-xl group-hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={scrollToPricing} data-id="element-156" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight" data-id="element-157">
                  {bonus.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-158">
                  {bonus.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Pricing Section - Física */}
      <section id="compra" className="py-6 px-4 bg-gradient-to-b from-blue-50 to-white scroll-mt-2" data-id="element-159">
        <div className="container mx-auto max-w-4xl" data-id="element-160">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInScale} data-id="element-161">
            <div className="bg-white rounded-3xl shadow-2xl border-2 border-blue-200 overflow-hidden" data-id="element-162">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-6 text-center" data-id="element-163">
                <div className="flex items-center justify-center gap-2 mb-1" data-id="element-164">
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-165" />
                  <span className="text-sm font-bold uppercase tracking-wide" data-id="element-166">
                    Oferta Especial por Tiempo Limitado
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-black" data-id="element-167">
                  MEGA PACK FÍSICA
                </h2>
                <p className="text-sm text-blue-100 mt-1" data-id="element-168">
                  Sistema completo de Física + 3 Bonos de Regalo
                </p>
              </div>

              <div className="p-6 md:p-8" data-id="element-169">
                <div className="text-center mb-6" data-id="element-170">
                  <div className="mb-4" data-id="element-171">
                    <p className="text-sm text-gray-500 mb-2" data-id="element-172">
                      Precio regular:
                    </p>
                    <p className="text-3xl text-gray-400 line-through font-bold mb-1" data-id="element-173">
                      $49.99 USD
                    </p>
                  </div>

                  <div className="mb-4" data-id="element-174">
                    <p className="text-lg text-gray-700 font-semibold mb-2" data-id="element-175">
                      Tu precio especial:
                    </p>
                    <div className="flex items-center justify-center gap-1" data-id="element-176">
                      <span className="text-2xl text-gray-700 font-bold" data-id="element-177">
                        $
                      </span>
                      <span className="text-6xl md:text-7xl font-black text-blue-600" data-id="element-178">
                        13.99
                      </span>
                      <span className="text-xl text-gray-700 font-bold self-start mt-3" data-id="element-179">
                        USD
                      </span>
                    </div>
                  </div>

                  <div className="inline-block bg-green-100 border-2 border-green-300 rounded-xl px-4 py-2" data-id="element-180">
                    <p className="text-green-700 font-bold" data-id="element-181">
                      ✓ Ahorras $36.00 USD hoy
                    </p>
                  </div>
                </div>

                <div className="mb-6" data-id="element-182">
                  <a href="https://checkout.edulabs.online/cart/45637812748449:1" target="_blank" rel="noopener noreferrer" className="group block w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-black text-lg md:text-xl uppercase py-4 px-6 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] border-b-4 border-blue-900 text-center" data-id="element-183">
                    <span className="flex items-center justify-center gap-2" data-id="element-184">
                      SI, QUIERO EL MEGA PACK FÍSICA
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-185" />
                    </span>
                  </a>
                  <p className="text-center text-xs text-gray-500 mt-2 flex items-center justify-center gap-1" data-id="element-186">
                    <Lock className="w-3 h-3" data-id="element-187" />
                    Pago único • Acceso de por vida • 100% Seguro
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6 text-sm" data-id="element-188">
                  {[{
                  icon: Download,
                  text: 'Descarga inmediata'
                }, {
                  icon: Award,
                  text: 'Acceso ilimitado'
                }, {
                  icon: ShieldCheck,
                  text: 'Garantía 7 días'
                }, {
                  icon: Sparkles,
                  text: '3 Bonos gratis'
                }, {
                  icon: Check,
                  text: 'Sin suscripciones'
                }, {
                  icon: Lock,
                  text: 'Pago seguro'
                }].map((item, i) => <div key={i} className="flex items-center gap-2 text-gray-700" data-id="element-189">
                      <div className="bg-green-100 p-1.5 rounded-lg flex-shrink-0" data-id="element-190">
                        <item.icon className="w-4 h-4 text-green-600" data-id="element-191" />
                      </div>
                      <span className="font-medium text-xs" data-id="element-192">{item.text}</span>
                    </div>)}
                </div>

                <div className="flex items-center justify-center gap-4 opacity-40 grayscale pt-4 border-t border-gray-100" data-id="element-193">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png" alt="Visa" className="h-4" data-id="element-194" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png" alt="Mastercard" className="h-4" data-id="element-195" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/2560px-PayPal.svg.png" alt="PayPal" className="h-4" data-id="element-196" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SUPER MEGA PACK - Física + Química - DIRECTAMENTE DESPUÉS SIN GARANTÍA EN MEDIO */}
      <section className="py-8 px-4 bg-gradient-to-b from-white to-green-50" data-id="element-197">
        <div className="container mx-auto max-w-4xl" data-id="element-198">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInScale} data-id="element-199">
            {/* Badge "Más Popular" - MOVIDO ARRIBA COMPLETAMENTE */}
            <div className="text-center mb-4" data-id="element-200">
              <div className="inline-block bg-gradient-to-r from-orange-500 to-red-500 text-white font-black px-5 py-2 rounded-full text-xs shadow-lg" data-id="element-201">
                ⭐ MÁS POPULAR
              </div>
            </div>

            {/* Badge de upgrade */}
            <div className="text-center mb-6" data-id="element-202">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-full font-black text-sm uppercase tracking-wider shadow-xl animate-pulse" data-id="element-203">
                <Sparkles className="w-5 h-5" data-id="element-204" />
                <span data-id="element-205">¡MEJORA TU COMPRA!</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl shadow-2xl border-2 border-green-300 overflow-hidden" data-id="element-206">
              {/* Header */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white py-5 px-6 text-center" data-id="element-207">
                <div className="flex items-center justify-center gap-2 mb-2" data-id="element-208">
                  <Plus className="w-6 h-6" data-id="element-209" />
                  <span className="text-lg font-black uppercase tracking-wide" data-id="element-210">
                    Super Mega Pack Completo
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-black mb-1" data-id="element-211">
                  FÍSICA + QUÍMICA
                </h2>
                <p className="text-sm text-green-100" data-id="element-212">
                  Ambos sistemas completos + Todos los bonos
                </p>
              </div>

              <div className="p-6 md:p-8" data-id="element-213">
                {/* Imagen del producto */}
                <div className="flex justify-center mb-6" data-id="element-214">
                  <div className="relative group" data-id="element-215">
                    <div className="absolute -inset-4 bg-gradient-to-r from-green-400 to-emerald-600 rounded-3xl opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-500" data-id="element-216"></div>
                    <img src="https://cdn.magicpatterns.com/uploads/wYuTVFqjh7r6ynLs4ozpah/Diseno_sin_titulo_(6).png" alt="Super Mega Pack Física y Química" className="relative w-full max-w-md rounded-2xl shadow-2xl" data-id="element-217" />
                  </div>
                </div>

                {/* Qué incluye */}
                <div className="bg-white rounded-2xl p-6 mb-6 border-2 border-green-200" data-id="element-218">
                  <h3 className="text-xl font-black text-green-700 mb-4 text-center" data-id="element-219">
                    ✨ Incluye Ambos Productos Completos:
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-220">
                    <div className="flex items-start gap-3" data-id="element-221">
                      <div className="bg-blue-100 p-2 rounded-lg mt-0.5" data-id="element-222">
                        <FlaskConical className="w-5 h-5 text-blue-600" data-id="element-223" />
                      </div>
                      <div data-id="element-224">
                        <p className="font-bold text-gray-900" data-id="element-225">
                          Mega Pack FÍSICA
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-226">
                          Sistema completo + 3 bonos
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-227">
                      <div className="bg-green-100 p-2 rounded-lg mt-0.5" data-id="element-228">
                        <FlaskConical className="w-5 h-5 text-green-600" data-id="element-229" />
                      </div>
                      <div data-id="element-230">
                        <p className="font-bold text-gray-900" data-id="element-231">
                          Mega Pack QUÍMICA
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-232">
                          Sistema completo + 3 bonos
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Precio */}
                <div className="text-center mb-6" data-id="element-233">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-234">
                    Valor total si compras por separado:
                  </p>
                  <p className="text-2xl text-gray-400 line-through font-bold mb-3" data-id="element-235">
                    $27.98 USD
                  </p>

                  <div className="mb-4" data-id="element-236">
                    <p className="text-lg text-gray-700 font-semibold mb-2" data-id="element-237">
                      Tu precio especial del combo:
                    </p>
                    <div className="flex items-center justify-center gap-1" data-id="element-238">
                      <span className="text-2xl text-green-700 font-bold" data-id="element-239">
                        $
                      </span>
                      <span className="text-6xl md:text-7xl font-black text-green-600" data-id="element-240">
                        21.99
                      </span>
                      <span className="text-xl text-green-700 font-bold self-start mt-3" data-id="element-241">
                        USD
                      </span>
                    </div>
                  </div>

                  <div className="inline-block bg-green-100 border-2 border-green-400 rounded-xl px-5 py-2" data-id="element-242">
                    <p className="text-green-700 font-bold text-lg" data-id="element-243">
                      ✓ Ahorras $5.99 USD adicionales
                    </p>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="mb-6" data-id="element-244">
                  <a href="https://checkout.edulabs.online/cart/45638754533537:1" target="_blank" rel="noopener noreferrer" className="group block w-full bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white font-black text-lg md:text-xl uppercase py-5 px-6 rounded-xl shadow-2xl hover:shadow-green-500/50 transition-all duration-300 transform hover:scale-[1.02] border-b-4 border-green-900 text-center" data-id="element-245">
                    <span className="flex items-center justify-center gap-2" data-id="element-246">
                      SI, QUIERO EL SUPER MEGA PACK
                      <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-247" />
                    </span>
                  </a>
                  <p className="text-center text-xs text-gray-500 mt-2 flex items-center justify-center gap-1" data-id="element-248">
                    <Lock className="w-3 h-3" data-id="element-249" />
                    Pago único • Acceso de por vida • 100% Seguro
                  </p>
                </div>

                {/* Beneficios del combo */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm" data-id="element-250">
                  {[{
                  icon: Check,
                  text: '2 Sistemas completos'
                }, {
                  icon: Sparkles,
                  text: '6 Bonos incluidos'
                }, {
                  icon: Award,
                  text: 'Mejor precio'
                }, {
                  icon: Download,
                  text: 'Descarga inmediata'
                }, {
                  icon: ShieldCheck,
                  text: 'Garantía 7 días'
                }, {
                  icon: Lock,
                  text: 'Pago seguro'
                }].map((item, i) => <div key={i} className="flex items-center gap-2 text-gray-700" data-id="element-251">
                      <div className="bg-green-100 p-1.5 rounded-lg flex-shrink-0" data-id="element-252">
                        <item.icon className="w-4 h-4 text-green-600" data-id="element-253" />
                      </div>
                      <span className="font-medium text-xs" data-id="element-254">{item.text}</span>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Razones para elegir el combo */}
            <div className="mt-6 bg-white rounded-2xl p-6 border-2 border-green-200 shadow-lg" data-id="element-255">
              <h3 className="text-lg font-bold text-green-700 mb-4 text-center" data-id="element-256">
                ¿Por qué elegir el Super Mega Pack?
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm" data-id="element-257">
                <div className="text-center" data-id="element-258">
                  <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-259">
                    <TrendingUp className="w-6 h-6 text-green-600" data-id="element-260" />
                  </div>
                  <p className="font-bold text-gray-900" data-id="element-261">Ahorro máximo</p>
                  <p className="text-gray-600" data-id="element-262">
                    $5.99 USD menos que comprar por separado
                  </p>
                </div>
                <div className="text-center" data-id="element-263">
                  <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-264">
                    <BookOpen className="w-6 h-6 text-green-600" data-id="element-265" />
                  </div>
                  <p className="font-bold text-gray-900" data-id="element-266">Cobertura total</p>
                  <p className="text-gray-600" data-id="element-267">
                    Física y Química completas para todo el año
                  </p>
                </div>
                <div className="text-center" data-id="element-268">
                  <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-269">
                    <Sparkles className="w-6 h-6 text-green-600" data-id="element-270" />
                  </div>
                  <p className="font-bold text-gray-900" data-id="element-271">Doble valor</p>
                  <p className="text-gray-600" data-id="element-272">
                    6 bonos gratis en total (3 de cada pack)
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Garantía - MOVIDA DESPUÉS DE AMBAS OFERTAS */}
      <section className="py-8 px-4 bg-gradient-to-b from-green-50 to-blue-50" data-id="element-273">
        <div className="container mx-auto max-w-4xl" data-id="element-274">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInScale} data-id="element-275">
            <div className="flex items-center gap-4 bg-white p-6 rounded-2xl border-2 border-green-200 shadow-lg" data-id="element-276">
              <div className="flex-shrink-0 bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-xl" data-id="element-277">
                <ShieldCheck className="w-10 h-10 text-white" data-id="element-278" />
              </div>
              <div data-id="element-279">
                <h3 className="text-xl font-bold text-green-600 mb-2" data-id="element-280">
                  Garantía de Satisfacción Total
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-281">
                  Pruébalo{' '}
                  <span className="font-bold text-gray-900" data-id="element-282">7 días</span>. Si no
                  cumple tus expectativas, te devolvemos el{' '}
                  <span className="font-bold text-green-600" data-id="element-283">
                    100% de tu dinero
                  </span>
                  . Sin preguntas, sin complicaciones.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 px-4 bg-blue-50" data-id="element-284">
        <div className="container mx-auto max-w-6xl" data-id="element-285">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-286">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-287">
              Lo que dicen <span className="text-blue-600" data-id="element-288">otros docentes</span>
            </h2>
            <p className="text-xl text-gray-600" data-id="element-289">
              Resultados reales de profesores como tú
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-6 mb-6" data-id="element-290">
            <motion.div variants={staggerItem} data-id="element-291">
              <TestimonialCard name="María G." location="Bogotá, Colombia" quote="Ahora organizo todo el año sin estrés. Es increíble la cantidad de tiempo que ahorro." data-id="element-292" />
            </motion.div>
            <motion.div variants={staggerItem} data-id="element-293">
              <TestimonialCard name="Gustavo L." location="Ciudad de México, México" quote="Los estudiantes se involucraron más con los experimentos. Las clases cambiaron por completo." data-id="element-294" />
            </motion.div>
            <motion.div variants={staggerItem} data-id="element-295">
              <TestimonialCard name="Cecilia M." location="Madrid, España" quote="Es el único material que utilicé durante todo el año. Muy completo y profesional." data-id="element-296" />
            </motion.div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-297">
            <motion.div variants={staggerItem} data-id="element-298">
              <TestimonialCard name="Natalia F." location="Montevideo, Uruguay" quote="Las prácticas resultaron excelentes. Mis alumnos entienden mejor los conceptos." data-id="element-299" />
            </motion.div>
            <motion.div variants={staggerItem} data-id="element-300">
              <TestimonialCard name="Roberto P." location="Buenos Aires, Argentina" quote="Mis clases son más dinámicas y los alumnos participan activamente." data-id="element-301" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 px-4 bg-white" data-id="element-302">
        <div className="container mx-auto max-w-3xl" data-id="element-303">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12" data-id="element-304">
            Preguntas Frecuentes
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="space-y-4" data-id="element-305">
            {[{
            q: '¿En qué formato recibo el material?',
            a: '100% digital (PDF descargables) listos para usar desde cualquier dispositivo.'
          }, {
            q: '¿Puedo imprimir los cuadernillos?',
            a: 'Sí, están diseñados específicamente para imprimir o proyectar en clase. Sin restricciones.'
          }, {
            q: '¿Qué niveles abarca?',
            a: 'Desde 1.º a 6.º año de Educación Media / Secundaria / Bachillerato / ESO. Todo incluido.'
          }, {
            q: '¿Necesito laboratorio?',
            a: 'No. Las experiencias están pensadas para realizarse en el aula con materiales accesibles y económicos.'
          }, {
            q: '¿Cuánto tarda en llegar?',
            a: 'El acceso es inmediato. Recibes un correo electrónico con tus datos de acceso segundos después de la compra.'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} className="bg-blue-50 rounded-2xl p-6 shadow-sm border border-blue-100 hover:shadow-md transition-shadow" data-id="element-306">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-start gap-3" data-id="element-307">
                  <HelpCircle className="w-6 h-6 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-308" />
                  {item.q}
                </h3>
                <p className="text-gray-600 ml-9 leading-relaxed" data-id="element-309">{item.a}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 px-4 bg-blue-50 text-center relative overflow-hidden" data-id="element-310">
        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-311">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-312">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight text-gray-900" data-id="element-313">
              ¿Listo para transformar <br className="hidden md:block" data-id="element-314" />
              tu año escolar?
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-2xl mx-auto" data-id="element-315">
              Únete a cientos de docentes que ya organizaron su año de Física
              sin estrés
            </p>
            <CTAButton text="QUIERO EMPEZAR AHORA" subtext="Descarga inmediata • Garantía de 7 días" className="text-xl" data-id="element-316" />
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 text-center" data-id="element-317">
        <div className="container mx-auto max-w-4xl" data-id="element-318">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-319">
            <div className="bg-blue-600 p-2 rounded-xl" data-id="element-320">
              <FlaskConical className="w-6 h-6 text-white" data-id="element-321" />
            </div>
            <span className="text-white font-bold text-xl" data-id="element-322">
              MEGA PACK FÍSICA EN ACCIÓN™
            </span>
          </div>
          <p className="mb-6 text-sm" data-id="element-323">
            © 2026 Mega Pack Física en Acción™. Todos los derechos reservados.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm" data-id="element-324">
            <a href="#" className="hover:text-white transition-colors" data-id="element-325">
              Términos y Condiciones
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-326">
              Política de Privacidad
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-327">
              Soporte
            </a>
          </div>
        </div>
      </footer>
    </div>;
};